import React, { useState, useEffect } from "react";
import { 
  Calendar, 
  MapPin, 
  Video, 
  Clock, 
  ChevronRight, 
  PlusCircle,
  Search,
  X,
  Stethoscope,
  ChevronDown
} from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import { useToast } from "../../context/ToastContext";
import { db } from "../../firebase/config";
import { ref, onValue, set } from "firebase/database";
import PatientSidebar from "../../components/PatientSidebar";
import { generateClinicalId } from "../../utils/idGenerator";
import "./Appointments.css";

const Appointments = () => {
  const { currentUser, userData } = useAuth();
  const { showAlert } = useToast();
  const [isBookingModalOpen, setIsBookingModalOpen] = useState(false);
  const [upcomingAppointments, setUpcomingAppointments] = useState([]);
  const [doctors, setDoctors] = useState([]);
  const [appointmentData, setAppointmentData] = useState({
    doctorId: "", 
    doctorCustomId: "",
    doctorName: "",
    specialty: "",
    date: "",
    time: "",
    type: "Clinic Visit"
  });

  useEffect(() => {
    if (!currentUser) return;

    // 1. Synchronize Session Registry
    const appointmentsRef = ref(db, `appointments`);
    const unsubscribeAppts = onValue(appointmentsRef, (snapshot) => {
      const data = snapshot.val();
      if (data) {
        // Collect appointments where this patient is involved (correctly mapping the registry)
        const allAppts = Object.entries(data)
          .map(([id, val]) => ({
            id,
            ...val,
            icon: val.type === "Video Consultation" ? Video : MapPin
          }))
          .filter(appt => appt.patientUid === currentUser.uid);
        
        setUpcomingAppointments(allAppts.sort((a, b) => new Date(a.date) - new Date(b.date)));
      } else {
        setUpcomingAppointments([]);
      }
    });

    // 2. Synchronize Clinical Staff Feed
    const usersRef = ref(db, "users");
    const unsubscribeStaff = onValue(usersRef, (snapshot) => {
      const data = snapshot.val();
      if (data) {
        const staffList = Object.entries(data)
          .filter(([uid, val]) => {
            const role = val.role?.toLowerCase() || "";
            return role === "doctor" || role === "physician";
          })
          .map(([uid, val]) => ({ 
            uid, 
            ...val,
            specialty: val.specialty || "General Medicine"
          }));
        setDoctors(staffList);
      }
    });

    return () => {
      unsubscribeAppts();
      unsubscribeStaff();
    };
  }, [currentUser]);

  // 3. Dynamic Clinical Staff Initialization
  useEffect(() => {
    if (doctors.length > 0 && !appointmentData.doctorId) {
      const firstDoc = doctors[0];
      setAppointmentData(prev => ({
        ...prev,
        doctorId: firstDoc.uid,
        doctorCustomId: firstDoc.customId,
        doctorName: firstDoc.name,
        specialty: firstDoc.specialty || "GEN_PHYSICIAN"
      }));
    }
  }, [doctors, appointmentData.doctorId]);

  const handleConfirmBooking = async () => {
    if (!appointmentData.date || !appointmentData.time || !appointmentData.doctorId) {
      showAlert("PROTOCOL_ERROR: Clinical identity, date, and time must be specified.", "error");
      return;
    }

    try {
      // Provision Unique Appointment Identity
      const aptId = await generateClinicalId("appointment");
      
      // Clinical Identity Linkage
      // We store appointments globally indexed by APT- code for better lookup
      const appointmentRef = ref(db, `appointments/${aptId}`);
      
      const payload = {
        appointmentId: aptId,
        doctor: appointmentData.doctorName,
        doctorId: appointmentData.doctorId,
        doctorCustomId: appointmentData.doctorCustomId,
        patientUid: currentUser.uid,
        patientName: userData?.name || "Patient",
        patientCustomId: userData?.customId || "PT-PENDING",
        specialty: appointmentData.specialty,
        date: appointmentData.date,
        time: appointmentData.time,
        type: appointmentData.type,
        status: "Confirmed",
        address: appointmentData.type === "Video Consultation" ? "Online / Video Link" : "123 Health Ave, Suite 400",
        createdAt: new Date().toISOString()
      };

      await set(appointmentRef, payload);
      
      setIsBookingModalOpen(false);
      showAlert(`SESSION_PROVISIONED: Appointment ${aptId} active in registry.`, "success");
    } catch (error) {
      showAlert("REGISTRY_ERROR: " + error.message, "error");
    }
  };

  const [viewDate, setViewDate] = useState(new Date());

  const handlePrevMonth = () => setViewDate(prev => new Date(prev.getFullYear(), prev.getMonth() - 1, 1));
  const handleNextMonth = () => setViewDate(prev => new Date(prev.getFullYear(), prev.getMonth() + 1, 1));

  return (
    <div className="dashboard-shell">
      <PatientSidebar />

      <main className="dashboard-content">
        <header className="appointments-header">
          <div className="header-text">
            <h1>My Appointments</h1>
            <p>View and manage your upcoming health sessions</p>
          </div>
          <button className="premium-btn primary" onClick={() => setIsBookingModalOpen(true)}>
            <PlusCircle size={18} />
            Book New Appointment
          </button>
        </header>

        <div className="scrollable-content appointment-grid">
          <div className="appointment-view-controls">
            <div className="search-box glass-card">
              <Search size={18} className="icon-dim" />
              <input type="text" placeholder="Search by doctor or specialty..." />
            </div>
            
            <div className="filter-tabs">
              <button className="filter-tab active">Upcoming</button>
              <button className="filter-tab">Past</button>
              <button className="filter-tab">Cancelled</button>
            </div>
          </div>

          <section className="upcoming-section">
            <h2 className="section-subtitle">Confirmed Sessions</h2>
            <div className="appointments-list">
              {upcomingAppointments.map((appt) => {
                const TypeIcon = appt.icon;
                return (
                  <div key={appt.id} className="appointment-large-card glass-card">
                    <div className="appt-date-box">
                      {appt.date ? (
                        <>
                          <span className="month">
                            {new Date(appt.date).toLocaleDateString('en-US', { month: 'short' }).toUpperCase()}
                          </span>
                          <span className="day">
                            {new Date(appt.date).toLocaleDateString('en-US', { day: '2-digit' })}
                          </span>
                        </>
                      ) : (
                        <>
                          <span className="month">TBD</span>
                          <span className="day">??</span>
                        </>
                      )}
                    </div>
                    
                    <div className="appt-main-info">
                      <div className="header-row">
                        <div className="doc-meta">
                          <h3>{appt.doctor}</h3>
                          <span>{appt.specialty}</span>
                        </div>
                        <span className="status-badge confirmed">{appt.status}</span>
                      </div>
                      
                      <div className="details-row">
                        <div className="detail-item">
                          <Clock size={16} />
                          <span>{appt.time}</span>
                        </div>
                        <div className="detail-item">
                          <TypeIcon size={16} />
                          <span>{appt.type}</span>
                        </div>
                        <div className="detail-item">
                          <MapPin size={16} />
                          <span>{appt.address}</span>
                        </div>
                      </div>
                    </div>
                    
                    <button className="appt-action-btn">
                      <ChevronRight size={24} />
                    </button>
                  </div>
                );
              })}
            </div>
          </section>

          <section className="calendar-overview">
            <div className="calendar-card glass-card">
              <div className="cal-header">
                <h3>{viewDate.toLocaleString('en-US', { month: 'long', year: 'numeric' })}</h3>
                <div className="cal-nav">
                  <button className="cal-btn" onClick={handlePrevMonth}>Prev</button>
                  <button className="cal-btn" onClick={handleNextMonth}>Next</button>
                </div>
              </div>
              <div className="cal-mock-grid">
                {/* Dynamic Session Synchronization Grid */}
                {(() => {
                  const year = viewDate.getFullYear();
                  const month = viewDate.getMonth();
                  const firstDay = new Date(year, month, 1).getDay();
                  const daysInMonth = new Date(year, month + 1, 0).getDate();
                  
                  // Fill empty days for the first week
                  const grid = [];
                  for (let i = 0; i < firstDay; i++) {
                    grid.push(<div key={`empty-${i}`} className="cal-day empty"></div>);
                  }
                  
                  // Fill actual month days
                  for (let day = 1; day <= daysInMonth; day++) {
                    const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
                    const hasAppt = upcomingAppointments.some(appt => appt.date === dateStr);
                    
                    grid.push(
                      <div key={day} className={`cal-day ${hasAppt ? "has-event" : ""}`}>
                        <span>{day}</span>
                        {hasAppt && <div className="event-dot"></div>}
                      </div>
                    );
                  }
                  return grid;
                })()}
              </div>
            </div>
          </section>
        </div>

        {/* Booking Modal */}
        {isBookingModalOpen && (
          <div className="modal-backdrop">
            <div className="booking-modal-card glass-card">
              <div className="modal-header">
                <div className="modal-title-box">
                  <Stethoscope size={24} className="icon-blue" />
                  <h2>Book Appointment</h2>
                </div>
                <button className="close-btn" onClick={() => setIsBookingModalOpen(false)}>
                  <X size={20} />
                </button>
              </div>

              <div className="modal-body">
                <div className="input-group">
                  <label>Select Physician</label>
                  <div className="select-wrapper">
                    <select
                      value={appointmentData.doctorId}
                      onChange={(e) => {
                        const doc = doctors.find(d => d.uid === e.target.value);
                        if (doc) {
                          setAppointmentData({
                            ...appointmentData, 
                            doctorId: doc.uid,
                            doctorCustomId: doc.customId,
                            doctorName: doc.name,
                            specialty: doc.specialty || "GEN_PHYSICIAN"
                          });
                        }
                      }}
                    >
                      {doctors.length === 0 ? (
                        <option value="">SYNCHRONIZING_STAFF_REGISTRY...</option>
                      ) : (
                        doctors.map((doc) => (
                          <option key={doc.uid} value={doc.uid}>
                            {doc.name} - {doc.specialty || "GENERAL_PRACTICE"} ({doc.customId})
                          </option>
                        ))
                      )}
                    </select>
                    <ChevronDown size={16} className="select-icon" />
                  </div>
                </div>

                <div className="modal-form-row">
                  <div className="input-group">
                    <label>Preferred Date</label>
                    <input 
                      type="date" 
                      value={appointmentData.date}
                      onChange={(e) => setAppointmentData({...appointmentData, date: e.target.value})}
                    />
                  </div>
                  <div className="input-group">
                    <label>Preferred Time</label>
                    <input 
                      type="time" 
                      value={appointmentData.time}
                      onChange={(e) => setAppointmentData({...appointmentData, time: e.target.value})}
                    />
                  </div>
                </div>

                <div className="input-group">
                  <label>Consultation Type</label>
                  <div className="type-options">
                    <button 
                      className={`type-btn ${appointmentData.type === "Clinic Visit" ? "active" : ""}`}
                      onClick={() => setAppointmentData({...appointmentData, type: "Clinic Visit"})}
                    >
                      <MapPin size={16} />
                      Clinic Visit
                    </button>
                    <button 
                      className={`type-btn ${appointmentData.type === "Video Consultation" ? "active" : ""}`}
                      onClick={() => setAppointmentData({...appointmentData, type: "Video Consultation"})}
                    >
                      <Video size={16} />
                      Video Call
                    </button>
                  </div>
                </div>

                <div className="modal-footer">
                  <button className="cancel-btn" onClick={() => setIsBookingModalOpen(false)}>Cancel</button>
                  <button className="premium-btn primary" onClick={handleConfirmBooking}>
                    Confirm Booking
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default Appointments;
